(ns practitest.frontend.components2.dashboard.charts.render
  (:require ["d3" :as d3]
            [malli.core :as m]
            [practitest.frontend.components2.dashboard.charts.common :as c]
            [practitest.frontend.components2.dashboard.charts.scales :as scales]
            [practitest.frontend.components2.dashboard.charts.marks :as marks]
            [practitest.frontend.components2.dashboard.charts.axis :as axis]
            [practitest.frontend.components2.dashboard.charts.tooltip :as tooltip]))

(def defaults
  {:vector-effect "non-scaling-stroke"})

(def cartesian-defaults
  {:width   800
   :height  400
   :padding {:top 5 :right 20 :bottom 50 :left 25}
   :encode  {:x {:axis {:orient :bottom}}
             :y {:axis {:orient :left}}}})

(defn- infer-coord-system [{{:keys [x y theta radius]} :encode}]
  (cond
    (or theta radius) :polar
    (or x y)          :cartesian))

(defn- prepare-marks [{:keys [mark marks] :as spec}]
  (cond-> spec
    (and (not marks) mark)
    (->
      (assoc :marks [(if (keyword? mark) {:type mark} mark)])
      (dissoc :mark))))

(defn- build-cartesian-ctx [spec]
  (-> (c/deep-merge cartesian-defaults spec)
      scales/with-scales))

(defn setup-svg!
  "Apply svg-level attrs: viewBox sized to outer dims, plus a <style> block
   that applies :vector-effect to all descendant paths."
 [svg-el {:keys [vb-w vb-h vector-effect]}]
  (let [svg (-> (.select d3 svg-el)
                (.attr "viewBox" (str "0 0 " vb-w " " vb-h)))]
    (when vector-effect
      (c/ensure-style! svg (str "path { vector-effect: " vector-effect "; }")))
    svg))

(defn cartesian-layout!
  "Ensure chart layout groups exist on the svg and position them.
   Layout:
     .chart       — translated by (padding.left, padding.top)
       .y-axis    — at (0, 0)
       .x-axis    — at (0, height)
       .marks     — at (0, 0), data marks go here
       .caption   — at (0, height + caption-top-offset), when spec has :caption
       .legend    — at (0, height + encode.color.legend.offset)"
  [svg {:keys [padding height encode caption]}]
  (let [legend-cfg     (get-in encode [:color :legend])
        legend-offset  (or (:offset legend-cfg) 20)
        caption-height (if caption 15 0)
        g-chart        (-> (c/ensure-group! svg "chart")
                           (c/translate! (:left padding) (:top padding)))]
    (c/ensure-group! g-chart "y-axis")
    (-> (c/ensure-group! g-chart "x-axis")
        (c/translate! 0 height))
    (c/ensure-group! g-chart "marks")
    (when caption
      (-> (c/ensure-group! g-chart "caption")
          (c/translate! 0 (+ height (:offset caption)))))
    (when legend-cfg
      (-> (c/ensure-group! g-chart "legend")
          (c/translate! 0 (+ height caption-height legend-offset))))
    (tooltip/setup-tooltip! g-chart)
    svg))

(defn- normalize-padding [p]
  (cond
    (number? p) {:top p :right p :bottom p :left p}
    (map? p)    p))

(defn- normalize-scales [spec]
  (update spec :encode update-vals
          (fn [{:keys [scale] :as channel}]
            (cond-> channel
              (vector? scale) (assoc :scale {:domain scale})))))

(defn- lift-color-palette [{:keys [color-palette] :as spec}]
  (cond-> spec
    color-palette
    (update-in [:encode :color :scale]
               (fn [s]
                 (-> s
                     (update :domain #(or % (vec (keys color-palette))))
                     (update :range  #(or % (vec (vals color-palette)))))))))

(defn- build-context [spec]
  (let [spec         (-> (prepare-marks spec)
                         normalize-scales
                         lift-color-palette
                         (update :padding normalize-padding))
        coord-system (infer-coord-system spec)
        cartesian?   (= coord-system :cartesian)]
    (-> (c/deep-merge defaults spec)
        (assoc :coord-system coord-system :cartesian? cartesian?)
        (cond-> cartesian? build-cartesian-ctx))))

(defn- resolve-spec-size
  "Resolve canvas and inner sizes with priority:
   spec value -> DOM fallback."
  [svg-el spec]
  (let [rect           (some-> svg-el .getBoundingClientRect)
        dom-w          (.-width rect)
        dom-h          (.-height rect)
        padding        (normalize-padding (or (:padding spec) (:padding cartesian-defaults)))
        legend-offset  (or (get-in spec [:encode :color :legend :offset]) 20)
        caption-height (if (:caption spec) 15 0)
        vb-w           (or (:width spec) dom-w)
        vb-h           (or (:height spec) dom-h)
        width          (- vb-w (or (:left padding) 0) (or (:right padding) 0))
        height         (- vb-h (or (:top padding) 0) (or (:bottom padding) 0) legend-offset caption-height)]
    (cond-> spec
      vb-w   (assoc :vb-w vb-w)
      vb-h   (assoc :vb-h vb-h)
      width  (assoc :width width)
      height (assoc :height height))))

(defn- render-legend!
  [svg ctx]
  (let [legend-cfg   (get-in ctx [:encode :color :legend])
        domain       (get-in ctx [:encode :color :scale :domain])
        range        (get-in ctx [:encode :color :scale :range])
        inactive-set (set (get-in legend-cfg [:opacity :one-of]))
        items        (mapv (fn [label]
                             {:label label
                              :color (get range (.indexOf domain label))
                              :inactive (contains? inactive-set label)})
                           domain)
        n            (count items)]
    (when (pos? n)
      (let [inner-width     (:width ctx)
            bar-height      12
            bar-gap         2
            label-height    24
            update-duration 350
            on-click        (:on-legend-click ctx)
            tooltip         (:tooltip ctx)
            legend-offset   (or (:offset legend-cfg) 20)
            chart-height    (:height ctx)
            item-width      (/ inner-width n)
            bar-width       (- item-width bar-gap)
            legend-g        (.select svg "g.chart g.legend")
            lbl-key         (fn [^js d] (.-label d))
            color-fn        (fn [^js d] (aget d "color"))
            class-fn        (fn [^js d] (str "legend-item" (when-not (aget d "inactive") " active")))
            sel             (-> legend-g
                                (.selectAll "g.legend-item")
                                (.data (clj->js items) lbl-key))
            entered         (-> (.enter sel)
                                (.append "g")
                                (.attr "class" class-fn)
                                (.attr "transform" (fn [_ i] (str "translate(" (* i item-width) ",0)")))
                                (.each (fn [^js d]
                                         (this-as this
                                           (let [g (.select d3 this)]
                                             (-> g
                                                 (.append "rect")
                                                 (.attr "x" 0)
                                                 (.attr "y" 0)
                                                 (.attr "width" bar-width)
                                                 (.attr "height" bar-height)
                                                 (.attr "fill" color-fn))
                                             (-> g
                                                 (.append "foreignObject")
                                                 (.attr "x" 0)
                                                 (.attr "y" bar-height)
                                                 (.attr "width" bar-width)
                                                 (.attr "height" label-height)
                                                 (.append "xhtml:div")
                                                 (.attr "class" "legend-label")
                                                 (.text (.-label d))))))))]

        ;; EXIT: fade out removed items
        (-> (.exit sel)
            (.transition)
            (.duration update-duration)
            (.style "opacity" 0)
            (.remove))

        ;; UPDATE: sync active class, position, width, text, fill
        (-> sel
            (.attr "class" class-fn)
            (.transition)
            (.duration update-duration)
            (.attr "transform" (fn [_ i] (str "translate(" (* i item-width) ",0)"))))
        (-> sel (.select "rect") (.attr "fill" color-fn) (.attr "width" bar-width))
        (-> sel (.select "foreignObject") (.attr "width" bar-width))
        (-> sel (.select ".legend-label") (.text (fn [^js d] (.-label d))))

        ;; Wire click on full merged selection (enter + update)
        (let [all (.merge entered sel)]
          (when on-click
            (.on all "click" (fn [^js _event ^js d]
                               (on-click (.-label d)))))

          ;; Tooltip events
          (when tooltip
            (let [{:keys [show! hide!]} tooltip]
              (-> all
                  (.on "mouseenter"
                       (fn [^js _event ^js d]
                         (let [i (.indexOf domain (.-label d))]
                           (show! {:category  (.-label d)
                                   :placement :above
                                   :anchor-x  (+ (* i item-width) (/ bar-width 2))
                                   :anchor-y  (+ chart-height legend-offset)}))))
                  (.on "mouseleave"
                       (fn [] (hide!)))))))))))

(defn- render-caption! [svg {:keys [width caption]}]
  (let [grp   (.select svg "g.chart g.caption")]
    (-> grp
        (.selectAll "text")
        (.data #js [(:text caption)])
        (.join "text")
        (.attr "class" "caption-text")
        (.attr "text-anchor" "middle")
        (.attr "x" (/ width 2))
        (.attr "y" 0)
        (.text (fn [^js d] d)))))

(defn render-chart! [svg-el spec]
  (let [spec (resolve-spec-size svg-el spec)
        ctx (build-context spec)
        svg (setup-svg! svg-el ctx)]
    (when (:cartesian? ctx)
      (cartesian-layout! svg ctx)
      (axis/render! svg ctx)
      (let [tooltip-spec (get-in ctx [:encode :tooltip])
            ctx          (if tooltip-spec
                           (let [tooltip-g (.node (.select svg "g.chart g.chart-tooltip"))]
                             (assoc ctx
                                    :tooltip      (tooltip/make-tooltip-api tooltip-g (:width ctx))
                                    :tooltip-spec tooltip-spec))
                           ctx)]
        (doseq [mark (:marks ctx)]
          (marks/render! svg mark ctx))
        (when (:caption ctx)
          (render-caption! svg ctx))
        (when (get-in ctx [:encode :color :legend])
          (render-legend! svg ctx))))
    ctx))
